import { useTranslation } from 'react-i18next';
import { useRevealAll } from '../hooks/useReveal';

interface Role {
  icon: string;
  title: string;
  subtitle: string;
  benefits: string[];
  cta: string;
  ctaHref: string;
}

const ROLES: Role[] = [
  {
    icon: '\uD83D\uDC68\u200D\u2695\uFE0F',
    title: 'Doctors & Physicians',
    subtitle: 'General Practitioners & Specialists',
    benefits: [
      'AI-assisted SOAP note generation',
      'Differential diagnosis support',
      'Digital prescriptions with QR codes',
      'MDC Ghana credential verification',
      'Smart appointment scheduling',
      'NHIS claims automation',
    ],
    cta: 'Join as a Doctor \u2192',
    ctaHref: '/register?role=doctor',
  },
  {
    icon: '\uD83D\uDC69\u200D\u2695\uFE0F',
    title: 'Nurses',
    subtitle: 'Registered Nurses & Midwives',
    benefits: [
      'Offer home nursing services',
      'Bed baths, wound care, medication admin',
      'GPS-tracked home visit bookings',
      'NMC Ghana verification',
      'Clinical documentation tools',
      'MoMo payment for services',
    ],
    cta: 'Join as a Nurse \u2192',
    ctaHref: '/register?role=nurse',
  },
  {
    icon: '\uD83D\uDC8A',
    title: 'Pharmacies',
    subtitle: 'Licensed Retail & Hospital Pharmacies',
    benefits: [
      'Digital prescription verification',
      'Drug interaction AI checker',
      'Real-time inventory management',
      'Signature-backed dispense audit',
      'NHIS pharmacy claims automation',
      'Delivery management system',
    ],
    cta: 'Register Your Pharmacy \u2192',
    ctaHref: '/register?role=pharmacist',
  },
  {
    icon: '\uD83E\uDDEA',
    title: 'Laboratories',
    subtitle: 'Diagnostic & Pathology Labs',
    benefits: [
      'LOINC-coded test catalog',
      'Specimen chain-of-custody tracking',
      'Critical value alerts to clinicians',
      'Result release with e-signature',
      'Patient-ready lab reports',
      'NHIS lab claims',
    ],
    cta: 'Register Your Lab \u2192',
    ctaHref: '/register?role=lab_tech',
  },
  {
    icon: '\uD83E\uDE7A',
    title: 'Clinics',
    subtitle: 'Private & Community Clinics',
    benefits: [
      'Multi-clinician scheduling',
      'Patient queue & check-in flow',
      'Consultation & SOAP note tools',
      'NHIS claims for outpatient visits',
      'Revenue & visit analytics',
      'Staff roles & permissions',
    ],
    cta: 'Register Your Clinic \u2192',
    ctaHref: '/register?role=facility&type=clinic',
  },
  {
    icon: '\uD83C\uDFE5',
    title: 'Hospitals',
    subtitle: 'Public, Private & Faith-Based Hospitals',
    benefits: [
      'Department & ward management',
      'Admissions, discharges, transfers',
      'Inpatient & outpatient workflows',
      'NHIS accreditation billing at scale',
      'EHR integration & HL7/FHIR export',
      'Executive analytics dashboard',
    ],
    cta: 'Register Your Hospital \u2192',
    ctaHref: '/register?role=facility&type=hospital',
  },
];

export default function Roles() {
  const { t } = useTranslation();
  const containerRef = useRevealAll();

  return (
    <section className="role-section" id="providers" ref={containerRef}>
      <div className="section-header reveal" style={{ marginBottom: 48 }}>
        <div className="section-tag">{t('roles.kicker')}</div>
        <h2 className="section-title">{t('roles.title')}</h2>
        <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {t('roles.subtitle')}
        </p>
      </div>

      <div className="role-grid">
        {ROLES.map((r) => (
          <div className="role-card reveal" key={r.title}>
            <div className="role-icon-wrap">{r.icon}</div>
            <div className="role-title">{r.title}</div>
            <div className="role-subtitle">{r.subtitle}</div>
            {/* TODO(i18n): translate role benefits */}
            <ul className="role-benefits">
              {r.benefits.map((b) => (
                <li key={b}>
                  <span className="role-check">{'\u2713'}</span>
                  {b}
                </li>
              ))}
            </ul>
            <a href={r.ctaHref} className="role-cta">{r.cta}</a>
          </div>
        ))}
      </div>
    </section>
  );
}
